import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { Action, configureStore, Middleware, ThunkAction } from "@reduxjs/toolkit";
import { ConfigureStoreOptions } from "@reduxjs/toolkit/dist/configureStore";
import { setupListeners } from "@reduxjs/toolkit/query";
import type { CurriedGetDefaultMiddleware } from "@reduxjs/toolkit/src/getDefaultMiddleware";
import { rtkQueryErrorLogger } from "api/middleware";
import { appUserInitialState, appUserSlice, AppUserState, hasUserStateCookie } from "api/slices/appUser";
import type { ExhState } from "api/slices/exhSlice";
import { createLogger } from "redux-logger";

import { queriesApi } from "../api";
import { exhSlice } from "../api/slices/exhSlice";
import { warningSlice } from "../api/slices/warningSlice";

const options: ConfigureStoreOptions = {
  reducer: {
    [queriesApi.reducerPath]: queriesApi.reducer,
    [appUserSlice.name]: appUserSlice.reducer,
    [warningSlice.name]: warningSlice.reducer,
    [exhSlice.name]: exhSlice.reducer,
  },
  middleware: (gDM: CurriedGetDefaultMiddleware) => {
    const middlewares = gDM();
    if (typeof window === "undefined" && process.env.REDUX_LOGGER === "true") {
      middlewares.push(
        createLogger({
          collapsed: true,
          duration: true,
          logger: console,
        }) as Middleware
      );
    }
    middlewares.push(rtkQueryErrorLogger);
    middlewares.push(queriesApi.middleware);
    return middlewares;
  },
  devTools: true,
};

export const makeStore = () => {
  const newStore = configureStore(options);
  setupListeners(newStore.dispatch);
  return newStore;
};
export const store = makeStore();

export type RootState = ReturnType<typeof store.getState>;
export type AppStore = ReturnType<typeof store.dispatch>;
export type AppDispatch = AppStore["dispatch"];
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action>;

export { appUserSlice } from "api/slices/appUser";
export {
  clearExhError,
  exhSlice,
  resetExhState,
  setExhConnected,
  setExhError,
  setExhLoading,
  setExhUser,
} from "api/slices/exhSlice";
export { warningSlice } from "api/slices/warningSlice";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const selectAppUser = (state: RootState): AppUserState => {
  if (!hasUserStateCookie()) {
    // Cookie missing → user probably logged out in another tab
    return appUserInitialState;
  }
  return state[appUserSlice.name];
};

// ExtraHorizon state selector
export const selectExhState = (state: RootState): ExhState => state[exhSlice.name];
